import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { hasVisitReportForField } from '../../registry-visit-reports';
import { useCampaignRegistries } from '../../use-campaign-registries';
import type { StageMessage } from './shared';

/**
 * "Emergencia" (VE) banner — corn only.
 *
 * Shown to advisor-type users (RV / consultant) when an active corn crop monitoring is in the
 * VE stage — `phenology === 'emergence'` with no vegetative stage yet (V1+ carries one) — and
 * no visit report yet covers this field. It nudges scheduling a visit to check the emergence.
 *
 * Returns `null` outside that stage, once a visit report exists for the field, or for
 * non-advisor users (the copy addresses "el productor", i.e. it's for whoever visits growers).
 */
export const useEmergenciaStageMessage = ({
    cropMonitoring,
    field,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();

    const corn = cropMonitoring?.cornCropMonitoring;
    const isCornEmergence =
        Boolean(cropMonitoring?.active) &&
        cropMonitoring?.crop === 'corn' &&
        corn?.phenology === 'emergence' &&
        !corn?.vegetativeStage;

    const showCandidate = isValidatedBayerUserType && isCornEmergence;

    const registries = useCampaignRegistries({
        fieldId: showCandidate ? field.id : undefined,
        cropMonitoring,
        enabled: showCandidate,
    });

    if (!showCandidate) return null;
    if (registries.fetchEnabled && registries.visitReportsQuery.isLoading) return null;

    const visitReports = registries.visitReportsQuery.data?.results ?? [];
    if (hasVisitReportForField(visitReports, field.id)) return null;

    return {
        title: _('El cultivo está emergiendo'),
        message: _('Agendá una visita al productor para controlar la emergencia.'),
        secondaryAction: { label: _('Crear informe'), action: 'create-visit-report' },
        primaryAction: { label: _('Agendar visita'), action: 'schedule-visit' },
    };
};
